import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setCustomerToken, clearCustomerToken } from "./config";

const AuthCtx = createContext(null);

// Belt-and-suspenders fallback for browsers that silently drop SameSite=None
// cookies during the Google OAuth round-trip: the backend appends the access
// token to the redirect URL as `#t=<jwt>`. Read it once, store it in
// localStorage so the axios interceptor replays it as `Authorization: Bearer`
// on the very next /auth/me request — then wipe the hash so we don't keep
// the token in the URL bar.
function consumeOAuthTokenFromHash() {
  try {
    if (typeof window === "undefined") return;
    const h = window.location.hash || "";
    const m = h.match(/(?:^|[#&])t=([^&]+)/);
    if (!m) return;
    setCustomerToken(m[1]);
    const cleanHash = h.replace(/(^|[#&])t=[^&]+&?/, "$1").replace(/^[#?&]+|&$/g, "");
    const newUrl = window.location.pathname + window.location.search + (cleanHash ? `#${cleanHash}` : "");
    window.history.replaceState({}, "", newUrl);
  } catch { /* swallow — fallback path; never block app boot */ }
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = guest, object = logged in
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Pick up Google OAuth fallback token from URL hash (covers browsers that
    // drop SameSite=None cookies during the cross-site OAuth chain).
    consumeOAuthTokenFromHash();
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password, rememberMe = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember_me: rememberMe });
    if (data?.access_token) setCustomerToken(data.access_token);
    setUser(data);
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    if (data?.access_token) setCustomerToken(data.access_token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearCustomerToken();
    setUser(null);
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
    }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, logout, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export { formatApiErrorDetail };
